class ProductDetailInfo extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.variants = [];
    this.variantChangeUnsubscriber = null;
  }

  connectedCallback() {
    this.loadVariants();
    this.initGallery();
    this.initQuantity();
    this.initBundles();
    this.initInfoCards();
    this.initDeliveryDate();
    this.initBuyNow();
    this.initSelectedBundle();
    this.initVariantPricing();
    this.initReviewsLink();
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
    if (this._lightboxKeyHandler) {
      document.removeEventListener('keydown', this._lightboxKeyHandler);
    }
  }

  loadVariants() {
    const json = this.querySelector('[data-pdi-variants]');
    if (!json) return;

    try {
      this.variants = JSON.parse(json.textContent);
    } catch {
      this.variants = [];
    }
  }

  getVariantById(variantId) {
    const id = parseInt(variantId, 10);
    return this.variants.find((variant) => variant.id === id);
  }

  calcDiscountPercent(compareAtPrice, price) {
    if (!compareAtPrice || compareAtPrice <= price) return 0;
    return Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
  }

  formatMoneyCents(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }

    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return format.replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  formatDiscountBadge(percent) {
    const template = this.dataset.discountTemplate || '{percent_discount}% RABATT';
    return template.replace(/\{percent_discount\}/g, String(percent));
  }

  updatePricingForVariant(variant) {
    if (!variant) return;

    this.dataset.unitPrice = String(variant.price);
    this.updateStockStatus(variant);

    const priceEl = this.querySelector('[data-pdi-price]');
    const compareEl = this.querySelector('[data-pdi-price-compare]');
    const discountBadge = this.querySelector('[data-pdi-discount-badge]');
    const showDiscountBadge = this.dataset.showDiscountBadge === 'true';
    const discountPercent = this.calcDiscountPercent(variant.compare_at_price, variant.price);

    if (priceEl) {
      priceEl.textContent = this.formatMoneyCents(variant.price);
    }

    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = this.formatMoneyCents(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    if (discountBadge && showDiscountBadge) {
      if (discountPercent > 0) {
        discountBadge.textContent = this.formatDiscountBadge(discountPercent);
        discountBadge.hidden = false;
      } else {
        discountBadge.hidden = true;
      }
    }

    this.querySelectorAll('[data-pdi-bundle]').forEach((bundle) => {
      const quantity = parseInt(bundle.dataset.quantity, 10) || 1;
      const discount = parseInt(bundle.dataset.bundleDiscount, 10) || 0;
      const bundleTotal = Math.round((variant.price * quantity * (100 - discount)) / 100);
      const bundleCompare = variant.price * quantity;
      const bundleSavePercent = this.calcDiscountPercent(bundleCompare, bundleTotal);

      bundle.dataset.bundlePrice = this.formatMoneyCents(bundleTotal);

      const bundlePriceEl = bundle.querySelector('.pdi__bundle-price');
      const bundleCompareEl = bundle.querySelector('[data-pdi-bundle-compare]');
      const bundleSaveEl = bundle.querySelector('[data-pdi-bundle-save]');
      const bundleSavePercentEl = bundle.querySelector('[data-pdi-bundle-save-percent]');

      if (bundlePriceEl) {
        bundlePriceEl.textContent = this.formatMoneyCents(bundleTotal);
      }

      if (bundleCompareEl) {
        if (bundleSavePercent > 0) {
          bundleCompareEl.textContent = this.formatMoneyCents(bundleCompare);
          bundleCompareEl.hidden = false;
        } else {
          bundleCompareEl.hidden = true;
        }
      }

      if (bundleSaveEl && bundleSavePercentEl) {
        if (bundleSavePercent > 0) {
          bundleSavePercentEl.textContent = `-${bundleSavePercent}%`;
          bundleSaveEl.hidden = false;
        } else {
          bundleSaveEl.hidden = true;
        }
      }
    });

    const selectedBundle = this.querySelector('[data-pdi-bundle].is-selected');
    const buyNowPrice = this.querySelector('[data-pdi-buy-now-price]');
    if (buyNowPrice && selectedBundle?.dataset.bundlePrice) {
      buyNowPrice.textContent = selectedBundle.dataset.bundlePrice;
    } else if (buyNowPrice) {
      const qtyInput = this.querySelector('[data-pdi-qty]');
      const qty = parseInt(qtyInput?.value, 10) || 1;
      buyNowPrice.textContent = this.formatMoneyCents(variant.price * qty);
    }
  }

  updateStockStatus(variant) {
    const stockStatus = this.querySelector('[data-pdi-stock-status]');
    if (!stockStatus || !variant) return;

    const threshold = parseInt(stockStatus.dataset.stockThreshold || '10', 10);
    const isLowStock =
      variant.available &&
      (variant.inventory_management !== 'shopify' ||
        (variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold));

    stockStatus.hidden = !isLowStock;
  }

  initVariantPricing() {
    const variantInput = this.querySelector('input[name="id"]');
    if (!variantInput) return;

    const handleVariantChange = () => {
      const variant = this.getVariantById(variantInput.value);
      this.updatePricingForVariant(variant);
    };

    variantInput.addEventListener('change', handleVariantChange);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data?.sectionId && event.data.sectionId !== this.sectionId) return;
        const variantId = event.data?.variant?.id || variantInput.value;
        this.updatePricingForVariant(this.getVariantById(variantId));
      });
    }
  }

  initSelectedBundle() {
    const selected = this.querySelector('[data-pdi-bundle].is-selected');
    const qtyInput = this.querySelector('[data-pdi-qty]');
    const buyNowPrice = this.querySelector('[data-pdi-buy-now-price]');
    if (!selected || !qtyInput) return;

    const quantity = parseInt(selected.dataset.quantity, 10) || 1;
    qtyInput.value = quantity;
    if (buyNowPrice && selected.dataset.bundlePrice) {
      buyNowPrice.textContent = selected.dataset.bundlePrice;
    }
  }

  initGallery() {
    const mainImage = this.querySelector('[data-pdi-main-image]');
    const galleryMain = this.querySelector('.pdi__gallery-main');
    const thumbs = this.querySelectorAll('[data-pdi-thumb]');
    const prevBtn = this.querySelector('[data-pdi-thumb-prev]');
    const nextBtn = this.querySelector('[data-pdi-thumb-next]');
    const zoomBtn = this.querySelector('[data-pdi-zoom]');
    const galleryJson = this.querySelector('[data-pdi-gallery-json]');

    let images = [];
    if (galleryJson) {
      try {
        images = JSON.parse(galleryJson.textContent);
      } catch {
        images = [];
      }
    }

    if (!images.length && mainImage?.src) {
      images = [{ src: mainImage.src, alt: mainImage.alt || '' }];
    }

    if (!mainImage || !images.length) return;

    let activeIndex = Array.from(thumbs).findIndex((thumb) => thumb.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = 0;

    const setActive = (index) => {
      activeIndex = index;
      const image = images[index];
      if (!image) return;

      mainImage.src = thumbs[index]?.dataset.fullSrc || image.src;
      mainImage.alt = image.alt || '';
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
      thumbs[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };

    const showPrevious = () => {
      if (images.length < 2) return;
      setActive((activeIndex - 1 + images.length) % images.length);
    };

    const showNext = () => {
      if (images.length < 2) return;
      setActive((activeIndex + 1) % images.length);
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => setActive(index));
    });

    prevBtn?.addEventListener('click', showPrevious);
    nextBtn?.addEventListener('click', showNext);

    if (galleryMain && images.length > 1) {
      let touchStartX = 0;
      let touchStartY = 0;

      galleryMain.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches[0];
          touchStartX = touch.screenX;
          touchStartY = touch.screenY;
        },
        { passive: true }
      );

      galleryMain.addEventListener(
        'touchend',
        (event) => {
          const touch = event.changedTouches[0];
          const deltaX = touch.screenX - touchStartX;
          const deltaY = touch.screenY - touchStartY;

          if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

          if (deltaX > 0) {
            showPrevious();
          } else {
            showNext();
          }
        },
        { passive: true }
      );
    }

    this.initLightbox(images, zoomBtn, () => activeIndex, setActive);
  }

  initLightbox(images, zoomBtn, getActiveIndex, setActive) {
    const lightbox = this.querySelector('[data-pdi-lightbox]');
    if (!lightbox || !images.length) return;

    if (!lightbox.dataset.moved) {
      lightbox.dataset.moved = 'true';
      document.body.appendChild(lightbox);
    }

    const lightboxImage = lightbox.querySelector('[data-pdi-lightbox-image]');
    const prevBtn = lightbox.querySelector('[data-pdi-lightbox-prev]');
    const nextBtn = lightbox.querySelector('[data-pdi-lightbox-next]');
    const closeBtns = lightbox.querySelectorAll('[data-pdi-lightbox-close]');
    const dialog = lightbox.querySelector('[data-pdi-lightbox-dialog]');

    const updateNavState = () => {
      const hasMultiple = images.length > 1;
      if (prevBtn) prevBtn.hidden = !hasMultiple;
      if (nextBtn) nextBtn.hidden = !hasMultiple;
    };

    const showImage = (index) => {
      const image = images[index];
      if (!image || !lightboxImage) return;

      lightboxImage.src = image.src;
      lightboxImage.alt = image.alt || '';
      setActive(index);
    };

    const open = () => {
      showImage(getActiveIndex());
      updateNavState();
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      if (typeof trapFocus === 'function') {
        trapFocus(lightbox, dialog);
      }
    };

    const close = () => {
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(zoomBtn);
      }
    };

    zoomBtn?.addEventListener('click', open);

    prevBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      const nextIndex = (getActiveIndex() - 1 + images.length) % images.length;
      showImage(nextIndex);
    });

    nextBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      const nextIndex = (getActiveIndex() + 1) % images.length;
      showImage(nextIndex);
    });

    closeBtns.forEach((button) => {
      button.addEventListener('click', close);
    });

    dialog?.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    this._lightboxKeyHandler = (event) => {
      if (lightbox.hidden) return;

      if (event.code === 'Escape') {
        close();
      } else if (event.code === 'ArrowLeft' && images.length > 1) {
        event.preventDefault();
        const nextIndex = (getActiveIndex() - 1 + images.length) % images.length;
        showImage(nextIndex);
      } else if (event.code === 'ArrowRight' && images.length > 1) {
        event.preventDefault();
        const nextIndex = (getActiveIndex() + 1) % images.length;
        showImage(nextIndex);
      }
    };

    document.addEventListener('keydown', this._lightboxKeyHandler);
  }

  initQuantity() {
    const input = this.querySelector('[data-pdi-qty]');
    const minus = this.querySelector('[data-pdi-qty-minus]');
    const plus = this.querySelector('[data-pdi-qty-plus]');
    const buyNowPrice = this.querySelector('[data-pdi-buy-now-price]');

    if (!input) return;

    const updateBuyNowPrice = () => {
      if (!buyNowPrice) return;
      const unitCents = parseInt(this.dataset.unitPrice || '0', 10);
      const qty = parseInt(input.value, 10) || 1;
      buyNowPrice.textContent = this.formatMoneyCents(unitCents * qty);
    };

    minus?.addEventListener('click', () => {
      const min = parseInt(input.min, 10) || 1;
      const val = Math.max(min, (parseInt(input.value, 10) || 1) - 1);
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      updateBuyNowPrice();
    });

    plus?.addEventListener('click', () => {
      const max = parseInt(input.max, 10) || 99;
      const val = Math.min(max, (parseInt(input.value, 10) || 1) + 1);
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      updateBuyNowPrice();
    });

    input.addEventListener('change', updateBuyNowPrice);
  }

  initBundles() {
    const bundles = this.querySelectorAll('[data-pdi-bundle]');
    const qtyInput = this.querySelector('[data-pdi-qty]');
    const buyNowPrice = this.querySelector('[data-pdi-buy-now-price]');

    if (!bundles.length || !qtyInput) return;

    bundles.forEach((bundle) => {
      bundle.addEventListener('click', () => {
        bundles.forEach((b) => b.classList.remove('is-selected'));
        bundle.classList.add('is-selected');

        const quantity = parseInt(bundle.dataset.quantity, 10) || 1;
        qtyInput.value = quantity;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));

        if (buyNowPrice && bundle.dataset.bundlePrice) {
          buyNowPrice.textContent = bundle.dataset.bundlePrice;
        }
      });
    });
  }

  initInfoCards() {
    this.querySelectorAll('[data-pdi-info-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const card = toggle.closest('[data-pdi-info-card]');
        const content = card?.querySelector('[data-pdi-info-content]');
        if (!card || !content) return;

        const isOpen = card.classList.toggle('is-open');
        content.hidden = !isOpen;
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        const icon = toggle.querySelector('[data-pdi-info-icon]');
        if (icon) icon.textContent = isOpen ? '−' : '+';
      });
    });
  }

  initDeliveryDate() {
    // Delivery-date computation lives in the shared assets/delivery-date.js
    // (also used by the cart page and cart drawer), so all three surfaces
    // agree on the same weekday. Make sure delivery-date.js is loaded
    // before this file.
    if (window.RWDeliveryDate) {
      window.RWDeliveryDate.render(this);
    }
  }

  initBuyNow() {
    const btn = this.querySelector('[data-pdi-buy-now]');
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!btn || !form) return;

    btn.addEventListener('click', () => {
      const input = form.querySelector('[name="id"]');
      const qty = form.querySelector('[name="quantity"]');
      if (!input?.value) return;

      const params = new URLSearchParams();
      params.set('id', input.value);
      params.set('quantity', qty?.value || '1');
      window.location.href = `/cart/${input.value}:${qty?.value || 1}`;
    });
  }

  formatMoney(amount) {
    return this.formatMoneyCents(Math.round(amount * 100));
  }

  initReviewsLink() {
    const link = this.querySelector('[data-pdi-reviews-link]');
    if (!link) return;

    this.localizeReviewsBadge(link);

    link.addEventListener('click', (event) => {
      const selector = link.getAttribute('href');
      if (!selector || !selector.startsWith('#')) return;

      const target =
        document.querySelector(selector) ||
        document.querySelector('.jdgm-review-widget');

      if (!target) return;

      event.preventDefault();

      const header = document.querySelector('.section-header');
      const headerOffset = header ? header.getBoundingClientRect().height : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 16;

      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  localizeReviewsBadge(container) {
    const replaceReviewsText = (root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();

      while (node) {
        if (/\breviews?\b/i.test(node.textContent)) {
          node.textContent = node.textContent
            .replace(/\breviews\b/gi, 'Bewertungen')
            .replace(/\breview\b/gi, 'Bewertung');
        }
        node = walker.nextNode();
      }
    };

    replaceReviewsText(container);

    const observer = new MutationObserver(() => replaceReviewsText(container));
    observer.observe(container, { childList: true, subtree: true, characterData: true });
  }
}

if (!customElements.get('product-detail-info')) {
  customElements.define('product-detail-info', ProductDetailInfo);
}
